import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { vendutoApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, Users, Banknote, Gauge, Receipt, PartyPopper, Upload, Info } from 'lucide-react';
import { toast } from 'sonner';
import VendutoHeader from '../components/venduto/VendutoHeader';
import PeriodPicker from '../components/venduto/PeriodPicker';
import MetricheTable from '../components/venduto/MetricheTable';
import { useVendutoPeriodo } from '../hooks/useVendutoPeriodo';
import { periodoRange, fmtEuro, fmtNum, fmtPct } from '../lib/venduto';

const KpiCard = ({ icon: Icon, label, valore, nota, testid }) => (
  <Card className="card-lift">
    <CardContent className="p-4">
      <div className="flex items-center gap-2 text-muted-foreground mb-1">
        <Icon className="w-4 h-4" />
        <span className="text-xs uppercase tracking-wide">{label}</span>
      </div>
      <p className="text-2xl font-bold font-mono-numbers leading-tight" data-testid={testid}>{valore}</p>
      {nota && <p className="text-xs text-muted-foreground mt-1">{nota}</p>}
    </CardContent>
  </Card>
);

const VendutoPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.ruolo === 'admin' || user?.is_super_admin;
  const [stato, setStato] = useState(null);
  const [sel, setSel] = useVendutoPeriodo(stato);
  const [dati, setDati] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    vendutoApi.getStato().then(setStato).catch((e) => {
      toast.error(e.response?.data?.detail || 'Errore nel caricamento dello stato archivio');
      setLoading(false);
    });
  }, []);

  const carica = useCallback(async () => {
    if (!sel || !stato) return;
    setLoading(true);
    try {
      const r = periodoRange(sel, stato);
      setDati(await vendutoApi.getMetriche(r.data_inizio, r.data_fine));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel calcolo delle metriche');
    } finally {
      setLoading(false);
    }
  }, [sel, stato]);

  useEffect(() => { carica(); }, [carica]);

  const range = sel && stato ? periodoRange(sel, stato) : null;
  const t = dati?.totale;

  return (
    <div className="min-h-screen pb-24">
      <VendutoHeader
        titolo="Controllo di Gestione"
        sottotitolo={stato?.vuoto ? 'Archivio vuoto' : `Archivio ${stato?.data_min || '—'} → ${stato?.data_max || '—'}`}
      />

      <main className="px-4 py-5 max-w-lg mx-auto space-y-4">
        {stato && sel && (
          <Card>
            <CardContent className="p-4">
              <PeriodPicker value={sel} onChange={setSel} archivio={stato} />
              {range && <p className="text-sm font-semibold mt-2">{range.label}</p>}
            </CardContent>
          </Card>
        )}

        {stato?.vuoto && (
          <Card className="border-dashed">
            <CardContent className="p-6 text-center space-y-3">
              <Info className="w-10 h-10 mx-auto text-muted-foreground" />
              <p className="font-semibold">Nessun dato di venduto in archivio</p>
              <p className="text-sm text-muted-foreground">
                Carica gli export .xlsx di Cassa in Cloud (scontrini, proforma e fatture dello stesso
                periodo) per vedere metriche, analisi e confronti.
              </p>
              {isAdmin && (
                <Button className="gap-2" onClick={() => navigate('/venduto/caricamento')} data-testid="vai-caricamento-btn">
                  <Upload className="w-4 h-4" /> Carica i file
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {loading && (
          <div className="flex justify-center py-10"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>
        )}

        {!loading && dati && !dati.vuoto && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <KpiCard icon={Banknote} label="Incasso" valore={fmtEuro(t.incasso)} testid="kpi-incasso"
                nota={`${fmtNum(t.giornate)} giornate di servizio`} />
              <KpiCard icon={Users} label="Coperti" valore={fmtNum(t.coperti)} testid="kpi-coperti"
                nota={`${fmtNum(t.coperti_medi, 1)} medi per servizio`} />
              <KpiCard icon={Gauge} label="Riempimento" valore={fmtPct(t.riempimento)} testid="kpi-riempimento"
                nota={`capienza media ${fmtNum(t.capienza_media, 0)}`} />
              <KpiCard icon={Receipt} label="Spesa/coperto" valore={fmtEuro(t.spesa_per_coperto, 2)} testid="kpi-spesa-coperto"
                nota={`scontrino medio ${fmtEuro(t.scontrino_medio, 2)}`} />
            </div>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Metriche per servizio</CardTitle>
              </CardHeader>
              <CardContent>
                <MetricheTable dati={dati} />
                <p className="text-[11px] text-muted-foreground mt-3">
                  Nel Totale i coperti, i documenti e l'incasso sono sommati; le medie sono ricalcolate
                  sull'insieme e le giornate di servizio contano le <strong>date distinte</strong>
                  {' '}(un giorno con pranzo e cena conta una volta sola). Il riempimento del Totale somma
                  i servizi sulla stessa sala, quindi può superare il 100%.
                </p>
              </CardContent>
            </Card>

            {dati.eventi?.documenti > 0 && (
              <Card className="border-stone-300 bg-secondary/60">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <PartyPopper className="w-4 h-4 text-primary" /> Eventi / fuori servizio
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-3 gap-3 text-center">
                  <div>
                    <p className="text-lg font-bold font-mono-numbers" data-testid="eventi-incasso">{fmtEuro(dati.eventi.incasso)}</p>
                    <p className="text-xs text-muted-foreground">Incasso</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold font-mono-numbers">{fmtNum(dati.eventi.coperti)}</p>
                    <p className="text-xs text-muted-foreground">Coperti</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold font-mono-numbers">{fmtNum(dati.eventi.documenti)}</p>
                    <p className="text-xs text-muted-foreground">Documenti</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {!loading && dati?.vuoto && !stato?.vuoto && (
          <Card className="border-amber-300 bg-amber-50">
            <CardContent className="p-5 text-sm text-amber-900">
              Nessun dato nel periodo selezionato. L'archivio copre da{' '}
              <strong className="font-mono-numbers">{stato?.data_min}</strong> a{' '}
              <strong className="font-mono-numbers">{stato?.data_max}</strong>.
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default VendutoPage;
